import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Alert } from 'react-bootstrap';
import { getAllMenus, createMenu, updateMenu, deleteMenu, getMenuList } from '../services/menuService';
import LoadingIndicator from '../components/LoadingIndicator';
import TreeSelect from '../components/TreeSelect';
import '../styles/ManageMenu.css';
import '../styles/TreeSelect.css';

/**
 * ManageMenu component for menu management
 */
const ManageMenu = () => {
  // State variables
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeMenuItems, setActiveMenuItems] = useState([]);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [accessError, setAccessError] = useState(false);
  
  // Function to set notification with auto-dismiss after 5 seconds
  const showNotification = (message, type = 'success') => {
    setNotification({
      show: true,
      message,
      type
    });
    
    // Auto-dismiss notification after 5 seconds
    setTimeout(() => {
      setNotification(prev => ({ ...prev, show: false }));
    }, 5000);
  };

  // Form states for Add Menu
  const [addForm, setAddForm] = useState({
    parentMenuId: 0,
    menuName: '',
    description: '',
    sequence: ''
  });
  const [addFormErrors, setAddFormErrors] = useState({});

  // Form states for Edit Menu
  const [editForm, setEditForm] = useState({
    menuId: '',
    menuName: '',
    description: '',
    sequence: '',
    isActive: '2' // Default to "Select"
  });
  const [editFormErrors, setEditFormErrors] = useState({});

  // Get URL parameters
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  // Check for notifications from URL parameters (similar to the ASP.NET QueryString checks)
  useEffect(() => {
    if (queryParams.get('Add') === 'True') {
      showNotification('Menu Details Inserted Successfully', 'success');
    } else if (queryParams.get('Edit') === 'True') {
      showNotification('Menu Details Updated Successfully', 'success');
    } else if (queryParams.get('IsActive') === 'True') {
      showNotification('Menu Details Deleted Successfully', 'success');
    }
  }, []);

  // Load menu data on component mount
  useEffect(() => {
    loadMenuData();
  }, []);

  /**
   * Load menu data from API
   */
  const loadMenuData = async () => {
    try {
      setLoading(true);
      const data = await getMenuList();
      
      // Ensure data is an array before setting state
      if (data && Array.isArray(data)) {
        setMenuItems(data);
        setActiveMenuItems(data.filter(menu => menu.isActive === 1));
      } else {
        // If data is not an array, initialize with empty arrays
        console.error('Menu data is not in expected format:', data);
        setMenuItems([]);
        setActiveMenuItems([]);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading menu data:', error);
      // Initialize with empty arrays on error
      setMenuItems([]);
      setActiveMenuItems([]);
      setLoading(false);
    }
  };
  
   
     /**
   * Handle form input change for Add Menu form
   * @param {Object} e - Event object
   */
  const handleAddFormChange = (e) => {
    const { name, value } = e.target;
    setAddForm({
      ...addForm,
      [name]: value
    });
  };

  /**
   * Handle parent menu selection change in Add Menu form
   * @param {Object} e - Event object
   */
  const handleParentMenuChange = (e) => {
    setAddForm({
      ...addForm,
      parentMenuId: e.target.value ? parseInt(e.target.value) : 0
    });
  };

  /**
   * Handle form input change for Edit Menu form
   * @param {Object} e - Event object
   */
  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm({
      ...editForm,
      [name]: value
    });
  };

  /**
   * Handle menu selection change in Edit Menu form
   * @param {Object} e - Event object
   */
  const handleMenuSelectChange = (e) => {
    const value = e.target.value;
    if (!value) return;
    
    // Get menu details directly from the existing menu items
    const selectedMenu = menuItems.find(menu => menu.menuId.toString() === value.toString());
    
    if (selectedMenu) {
      setEditForm({
        menuId: selectedMenu.menuId,
        menuName: selectedMenu.menuName,
        description: selectedMenu.description || selectedMenu.menuDescription,
        sequence: selectedMenu.sequence || selectedMenu.menuSeq,
        isActive: selectedMenu.isActive.toString()
      });
    } else {
      showNotification('Menu not found', 'danger');
    }
  };

  /**
   * Validate Add Menu form
   * @returns {boolean} Is form valid
   */
  const validateAddForm = () => {
    const errors = {};
    
    if (!addForm.menuName.trim()) {
      errors.menuName = 'Required';
    }
    
    if (!addForm.description.trim()) {
      errors.description = 'Required';
    }
    
    if (!addForm.sequence.trim()) {
      errors.sequence = 'Required';
    } else if (isNaN(addForm.sequence)) {
      errors.sequence = 'Sequence must be a number';
    }
    
    setAddFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Validate Edit Menu form
   * @returns {boolean} Is form valid
   */
  const validateEditForm = () => {
    const errors = {};
    
    if (!editForm.menuId) {
      errors.menuId = 'Required';
    }
    
    if (!editForm.menuName || (typeof editForm.menuName === 'string' && !editForm.menuName.trim())) {
      errors.menuName = 'Required';
    }
    
    if (!editForm.description || (typeof editForm.description === 'string' && !editForm.description.trim())) {
      errors.description = 'Required';
    }
    
    if (editForm.sequence === undefined || editForm.sequence === null || 
        (typeof editForm.sequence === 'string' && !editForm.sequence.trim())) {
      errors.sequence = 'Required';
    } else if (isNaN(Number(editForm.sequence))) {
      errors.sequence = 'Sequence must be a number';
    }
    
    if (editForm.isActive === '2') {
      errors.isActive = 'Required';
    }
    
    setEditFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Handle Add Menu form submission
   */
  const handleAddMenuSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateAddForm()) {
      return;
    }
    
    // Check if menu name already exists at the same parent level
    const menuExists = menuItems.some(menu => 
      menu.menuName.toLowerCase() === addForm.menuName.toLowerCase() && 
      menu.parentMenuId === addForm.parentMenuId
    );
    
    if (menuExists) {
      showNotification('Menu Name Already Exists', 'danger');
      return;
    }
    
    try {
      const menuData = {
        parentMenuId: addForm.parentMenuId,
        menuName: addForm.menuName,
        description: addForm.description,
        sequence: parseInt(addForm.sequence)
      };
      
      const response = await createMenu(menuData);
      
      if (response.success) {
        // Clear the form
        setAddForm({
          parentMenuId: 0,
          menuName: '',
          description: '',
          sequence: ''
        });
        
        // Show success notification with message from API
        showNotification(response.message || 'Menu Details Inserted Successfully', 'success');
        
        // Reload menu data
        loadMenuData();
        
        // Update URL with query parameter (similar to the ASP.NET redirect)
        window.history.replaceState({}, '', `${window.location.pathname}?Add=True`);
      } else {
        // Show error notification with errorMessage from API
        showNotification(response.errorMessage || 'Menu Details Insertion Failed', 'danger');
      }
    } catch (error) {
      console.error('Error creating menu:', error);
      showNotification('Error creating menu', 'danger');
    }
  };

  /**
   * Handle Edit Menu form submission
   */
  const handleEditMenuSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateEditForm()) {
      return;
    }
    
    // If menu name has changed, check if the new name already exists at the same parent level
    const selectedMenu = menuItems.find(menu => menu.menuId === editForm.menuId);
    if (selectedMenu && selectedMenu.menuName.toLowerCase() !== editForm.menuName.toLowerCase()) {
      const parentMenuId = selectedMenu.parentMenuId;
      const menuExists = menuItems.some(menu => 
        menu.menuName.toLowerCase() === editForm.menuName.toLowerCase() && 
        menu.parentMenuId === parentMenuId &&
        menu.menuId !== editForm.menuId
      );
      
      if (menuExists) {
        showNotification('Menu Name Already Exists', 'danger');
        return;
      }
    }
    
    try {
      const menuData = {
        menuName: editForm.menuName,
        description: editForm.description,
        sequence: parseInt(editForm.sequence),
        isActive: parseInt(editForm.isActive)
      };
      
      const response = await updateMenu(editForm.menuId, menuData);
      
      if (response.success) {
        // Clear the form
        setEditForm({
          menuId: '',
          menuName: '',
          description: '',
          sequence: '',
          isActive: '2'
        });
        
        // Show success notification with message from API
        showNotification(response.message || 'Menu Details Updated Successfully', 'success');
        
        // Reload menu data
        loadMenuData();
        
        // Update URL with query parameter (similar to the ASP.NET redirect)
        window.history.replaceState({}, '', `${window.location.pathname}?Edit=True`);
      } else {
        // Show error notification with errorMessage from API
        showNotification(response.errorMessage || 'Menu Details Update Failed', 'danger');
      }
    } catch (error) {
      console.error('Error updating menu:', error);
      showNotification('Error updating menu', 'danger');
    }
  };

  /**
   * Handle Delete (set inactive) for a menu item
   */
  const handleDeleteMenu = async () => {
    if (!addForm.parentMenuId) {
      showNotification('Please select a menu to delete', 'danger');
      return;
    }
    
    // Confirm delete
    if (!window.confirm('Are you sure you want to delete this menu?')) {
      return;
    }
    
    try {
      // In the original code, "delete" actually sets isActive=0
      const menuToDelete = menuItems.find(menu => menu.menuId === addForm.parentMenuId);
      
      if (!menuToDelete) {
        showNotification('Menu not found', 'danger');
        return;
      }
      
      const menuData = {
        menuName: menuToDelete.menuName,
        description: menuToDelete.menuDescription,
        sequence: menuToDelete.menuSeq,
        isActive: 0 // Set to inactive
      };
      
      const response = await updateMenu(addForm.parentMenuId, menuData);
      
      if (response.success) {
        // Clear the form
        setAddForm({
          parentMenuId: 0,
          menuName: '',
          description: '',
          sequence: ''
        });
        
        // Show success notification with message from API
        showNotification(response.message || 'Menu Details Deleted Successfully', 'success');
        
        // Reload menu data
        loadMenuData();
        
        // Update URL with query parameter (similar to the ASP.NET redirect)
        window.history.replaceState({}, '', `${window.location.pathname}?IsActive=True`);
      } else {
        // Show error notification with errorMessage from API
        showNotification(response.errorMessage || 'Menu Details Deletion Failed', 'danger');
      }
    } catch (error) {
      console.error('Error deleting menu:', error);
      showNotification('Error deleting menu', 'danger');
    }
  };

  /**
   * Clear Add Menu form
   */
  const clearAddForm = () => {
    setAddForm({
      parentMenuId: 0,
      menuName: '',
      description: '',
      sequence: ''
    });
    setAddFormErrors({});
  };

  /**
   * Clear Edit Menu form
   */
  const clearEditForm = () => {
    setEditForm({
      menuId: '',
      menuName: '',
      description: '',
      sequence: '',
      isActive: '2'
    });
    setEditFormErrors({});
  };

  // Show loading indicator while data is being fetched
  if (loading) {
    return <LoadingIndicator />;
  }

  // Show access error page if user doesn't have permission
  if (accessError) {
    return <div className="text-center p-5">You do not have permission to access this page.</div>;
  }

  return (
    <div className="elegant-aero">
      
      {/* Header */}

      <span className="span-title">Manage Menu</span>

      
      {/* Notification Area */}
      {notification.show && (
        <div className={`notification ${notification.type}`}>
          <span>{notification.message}</span>
          <button 
            type="button" 
            className="close-btn"
            onClick={() => setNotification({ ...notification, show: false })}
          >
            &times;
          </button>
        </div>
      )}
      
      {/* Main Content Area */}
      <div className="content">
        <div className="form-section">
          <div className="section-headers">
            <div className="section-header">Add Menu</div>
            <div className="section-header">Edit Menu</div>
          </div>
          
          <div className="form-grid">
            {/* Left Column - Add Menu Form */}
            <form className="form-column" onSubmit={handleAddMenuSubmit}>
              <div className="form-row">
                <label className="form-label">ParentMenu</label>
                <div className="form-input">
                  <TreeSelect
                    items={activeMenuItems}
                    selectedValue={addForm.parentMenuId}
                    onChange={handleParentMenuChange}
                    placeholder="Add to Root Child"
                    
                  />
                  {addFormErrors.parentMenuId && <div className="error-message">{addFormErrors.parentMenuId}</div>}
                </div>
              </div>
              
              <div className="form-row">
                <label className="form-label">Child Menu</label>
                <div className="form-input">
                  <input
                    type="text"
                    name="menuName"
                    value={addForm.menuName}
                    onChange={handleAddFormChange}
                    className={`text-input ${addFormErrors.menuName ? 'error' : ''}`}
                  />
                  {addFormErrors.menuName && <div className="error-message">{addFormErrors.menuName}</div>}
                </div>
              </div>
              
              <div className="form-row">
                <label className="form-label">Description</label>
                <div className="form-input">
                  <textarea
                    name="description"
                    value={addForm.description}
                    onChange={handleAddFormChange}
                    className={`textarea-input ${addFormErrors.description ? 'error' : ''}`}
                  ></textarea>
                  {addFormErrors.description && <div className="error-message">{addFormErrors.description}</div>}
                </div>
              </div>
              
              <div className="form-row">
                <label className="form-label">Sequence</label>
                <div className="form-input">
                  <input
                    type="text"
                    name="sequence"
                    value={addForm.sequence}
                    onChange={handleAddFormChange}
                    className={`text-input ${addFormErrors.sequence ? 'error' : ''}`}
                  />
                  {addFormErrors.sequence && <div className="error-message">{addFormErrors.sequence}</div>}
                </div>
              </div>
              
              <div className="form-row button-row">
                <div className="form-input">
                  <button type="submit" className="action-button">Save</button>
                  <button type="button" className="action-button" onClick={clearAddForm}>Cancel</button>
                </div>
              </div>
            </form>
            
            {/* Right Column - Edit Menu Form */}
            <form className="form-column" onSubmit={handleEditMenuSubmit}>
              <div className="form-row">
                <label className="form-label">Select Menu</label>
                <div className="form-input">
                  <TreeSelect
                    items={menuItems}
                    selectedValue={editForm.menuId ? parseInt(editForm.menuId) : ''}
                    onChange={handleMenuSelectChange}
                    placeholder="Select"
                    
                  />
                  {editFormErrors.menuId && <div className="error-message">{editFormErrors.menuId}</div>}
                </div>
              </div>
              
              <div className="form-row">
                <label className="form-label">Menu Name</label>
                <div className="form-input">
                  <input
                    type="text"
                    name="menuName"
                    value={editForm.menuName}
                    onChange={handleEditFormChange}
                    className={`text-input ${editFormErrors.menuName ? 'error' : ''}`}
                  />
                  {editFormErrors.menuName && <div className="error-message">{editFormErrors.menuName}</div>}
                </div>
              </div>
              
              <div className="form-row">
                <label className="form-label">Description</label>
                <div className="form-input">
                  <textarea
                    name="description"
                    value={editForm.description}
                    onChange={handleEditFormChange}
                    className={`textarea-input ${editFormErrors.description ? 'error' : ''}`}
                  ></textarea>
                  {editFormErrors.description && <div className="error-message">{editFormErrors.description}</div>}
                </div>
              </div>
              
              <div className="form-row">
                <label className="form-label">Sequence</label>
                <div className="form-input">
                  <input
                    type="text"
                    name="sequence"
                    value={editForm.sequence}
                    onChange={handleEditFormChange}
                    className={`text-input ${editFormErrors.sequence ? 'error' : ''}`}
                  />
                  {editFormErrors.sequence && <div className="error-message">{editFormErrors.sequence}</div>}
                </div>
              </div>
              
              <div className="form-row">
                <label className="form-label">IsActive</label>
                <div className="form-input">
                  <select
                    name="isActive"
                    value={editForm.isActive}
                    onChange={handleEditFormChange}
                    className={`dropdown-input ${editFormErrors.isActive ? 'error' : ''}`}
                  >
                    <option value="2">Select</option>
                    <option value="1">True</option>
                    <option value="0">False</option>
                  </select>
                  {editFormErrors.isActive && <div className="error-message">{editFormErrors.isActive}</div>}
                </div>
              </div>
              
              <div className="form-row button-row">
                <div className="form-input">
                  <button 
                    type="submit" 
                    className="action-button"
                    disabled={!editForm.menuId}
                  >
                    Update
                  </button>
                  <button type="button" className="action-button" onClick={clearEditForm}>Cancel</button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageMenu;